import { useCallback, useEffect, useRef, useState } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { CloseVisitsModal } from "../../components/admin/CloseVisitsModal";
import { OpenVisitsPanel } from "../../components/admin/OpenVisitsPanel";
import { SignaturePreviewModal } from "../../components/admin/SignaturePreviewModal";
import { VisitEditModal } from "../../components/admin/VisitEditModal";
import { VisitFiltersPanel } from "../../components/admin/VisitFiltersPanel";
import { VisitList } from "../../components/admin/VisitList";
import { LoadingBlock } from "../../components/common/LoadingBlock";
import { StatusBanner } from "../../components/common/StatusBanner";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  ADMIN_PAGE_SIZE,
  closeAdminVisits,
  fetchAdminOpenVisits,
  fetchAdminVisitsByIds,
  fetchAdminVisitsPage,
  fetchAllAdminVisits,
  updateAdminVisitRecord,
} from "../../services/admin";
import type { VisitFilters, VisitRecord } from "../../types/domain";
import { buildVisitsCsv, triggerCsvDownload } from "../../utils/csv";
import { formatDateForFilter, makeCsvFileName } from "../../utils/dateTime";
import { isAbortError, toErrorMessage } from "../../utils/errors";

const INITIAL_FILTERS: VisitFilters = {
  date: "",
  firstName: "",
  lastName: "",
  company: "",
};

const FILTER_DEBOUNCE_MS = 300;

type SignatureState = {
  path: string;
  label: string;
} | null;

type HistoryState = {
  records: VisitRecord[];
  total: number | null;
};

function replaceRecords(records: VisitRecord[], updates: VisitRecord[]) {
  const updatesById = new Map(updates.map((record) => [record.visitId, record]));
  return records.map((record) => updatesById.get(record.visitId) ?? record);
}

export function AdminDashboardPage() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const activeFilters = useDebouncedValue(filters, FILTER_DEBOUNCE_MS);
  const [openRecords, setOpenRecords] = useState<VisitRecord[]>([]);
  const [history, setHistory] = useState<HistoryState>({ records: [], total: null });
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  const [signatureState, setSignatureState] = useState<SignatureState>(null);
  const [editingRecord, setEditingRecord] = useState<VisitRecord | null>(null);
  const [editErrorMessage, setEditErrorMessage] = useState<string | null>(null);
  const [closingRecords, setClosingRecords] = useState<VisitRecord[] | null>(null);
  const [closeErrorMessage, setCloseErrorMessage] = useState<string | null>(null);
  const loadControllerRef = useRef<AbortController | null>(null);
  // Incrementato a ogni ricarica: scarta le pagine "Carica altre" arrivate dopo un cambio filtri.
  const historyVersionRef = useRef(0);

  const loadRecords = useCallback(async (nextFilters: VisitFilters) => {
    // Annulla eventuali richieste ancora in corso per filtri precedenti.
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    historyVersionRef.current += 1;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const [nextOpenRecords, firstPage] = await Promise.all([
        fetchAdminOpenVisits(nextFilters, controller.signal),
        fetchAdminVisitsPage(nextFilters, 0, ADMIN_PAGE_SIZE, controller.signal),
      ]);

      setOpenRecords(nextOpenRecords);
      setHistory(firstPage);
      setHasLoaded(true);
    } catch (error) {
      if (controller.signal.aborted || isAbortError(error)) {
        return;
      }

      setErrorMessage(
        toErrorMessage(error, "Impossibile recuperare l'archivio visite."),
      );
    } finally {
      if (loadControllerRef.current === controller) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadRecords(activeFilters).catch(() => undefined);
  }, [activeFilters, loadRecords]);

  useEffect(() => () => loadControllerRef.current?.abort(), []);

  async function handleLoadMore() {
    const historyVersion = historyVersionRef.current;
    setIsLoadingMore(true);
    setErrorMessage(null);

    try {
      const nextPage = await fetchAdminVisitsPage(
        activeFilters,
        history.records.length,
        ADMIN_PAGE_SIZE,
      );
      if (historyVersion !== historyVersionRef.current) {
        return;
      }

      setHistory((current) => {
        const knownIds = new Set(current.records.map((record) => record.visitId));
        return {
          records: [
            ...current.records,
            ...nextPage.records.filter((record) => !knownIds.has(record.visitId)),
          ],
          total: current.total,
        };
      });
    } catch (error) {
      setErrorMessage(toErrorMessage(error, "Impossibile caricare altre visite."));
    } finally {
      setIsLoadingMore(false);
    }
  }

  async function handleExport() {
    setIsExporting(true);
    setErrorMessage(null);

    try {
      const records = await fetchAllAdminVisits(filters);
      triggerCsvDownload(makeCsvFileName("registro-visite"), buildVisitsCsv(records));
    } catch (error) {
      setErrorMessage(toErrorMessage(error, "Esportazione CSV non riuscita."));
    } finally {
      setIsExporting(false);
    }
  }

  async function refreshOpenRecords() {
    try {
      setOpenRecords(await fetchAdminOpenVisits(activeFilters));
    } catch (error) {
      setErrorMessage(
        toErrorMessage(error, "Impossibile aggiornare l'elenco delle visite aperte."),
      );
    }
  }

  async function handleSaveEdit(payload: {
    visitId: string;
    visitorId: string;
    firstName: string;
    lastName: string;
    company: string;
    checkinAtIso: string;
    checkoutAtIso: string | null;
    notes: string | null;
  }) {
    if (!editingRecord) {
      return;
    }

    setIsSaving(true);
    setEditErrorMessage(null);

    try {
      const updatedRecord = await updateAdminVisitRecord(editingRecord, payload);
      setHistory((current) => ({
        ...current,
        records: replaceRecords(current.records, [updatedRecord]),
      }));
      setEditingRecord(null);
      await refreshOpenRecords();
    } catch (error) {
      setEditErrorMessage(
        toErrorMessage(error, "Salvataggio non riuscito. Verifica i dati inseriti."),
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleConfirmClose(checkoutAtIso: string) {
    if (!closingRecords) {
      return;
    }

    setIsSaving(true);
    setCloseErrorMessage(null);

    try {
      const closedIds = await closeAdminVisits(
        closingRecords.map((record) => record.visitId),
        checkoutAtIso,
      );
      const closedRecords = await fetchAdminVisitsByIds(closedIds);
      const closedIdSet = new Set(closedIds);

      setOpenRecords((current) =>
        current.filter((record) => !closedIdSet.has(record.visitId)),
      );
      setHistory((current) => ({
        ...current,
        records: replaceRecords(current.records, closedRecords),
      }));
      setClosingRecords(null);

      const skipped = closingRecords.length - closedIds.length;
      setNoticeMessage(
        `Uscita registrata per ${closedIds.length} ${closedIds.length === 1 ? "visita" : "visite"}.` +
          (skipped > 0 ? ` ${skipped} risultavano già chiuse.` : ""),
      );
    } catch (error) {
      setCloseErrorMessage(
        toErrorMessage(error, "Registrazione dell'uscita non riuscita."),
      );
    } finally {
      setIsSaving(false);
    }
  }

  const today = formatDateForFilter(new Date());
  const staleCount = openRecords.filter((record) => record.visitDate < today).length;
  const isRefreshing = isLoading && hasLoaded;
  const hasActiveFilters = Object.values(activeFilters).some((value) => value.trim() !== "");

  return (
    <AdminLayout
      title="Registro visite"
      subtitle="Consultazione, ricerca, export CSV e correzione dei record lato admin."
    >
      <section className="admin-stats" aria-label="Riepilogo">
        <div className="stat-tile">
          <span>Visite aperte</span>
          <strong>{hasLoaded ? openRecords.length : "—"}</strong>
        </div>
        <div className={`stat-tile ${staleCount > 0 ? "stat-tile--warning" : ""}`}>
          <span>Aperte da giorni precedenti</span>
          <strong>{hasLoaded ? staleCount : "—"}</strong>
        </div>
        <div className="stat-tile">
          <span>{hasActiveFilters ? "Visite trovate" : "Visite in archivio"}</span>
          <strong>{hasLoaded ? (history.total ?? history.records.length) : "—"}</strong>
        </div>
      </section>

      <VisitFiltersPanel
        filters={filters}
        onChange={setFilters}
        onClear={() => setFilters(INITIAL_FILTERS)}
        onRefresh={() => {
          setNoticeMessage(null);
          loadRecords(activeFilters).catch(() => undefined);
        }}
        onExport={handleExport}
        isBusy={isLoading}
        isExporting={isExporting}
      />

      {errorMessage ? <StatusBanner tone="error" message={errorMessage} /> : null}
      {noticeMessage ? <StatusBanner tone="success" message={noticeMessage} /> : null}

      {!hasLoaded && isLoading ? (
        <LoadingBlock label="Caricamento archivio visite..." />
      ) : null}

      {hasLoaded ? (
        <>
          <OpenVisitsPanel
            records={openRecords}
            isRefreshing={isRefreshing}
            onCloseVisits={(records) => {
              setCloseErrorMessage(null);
              setNoticeMessage(null);
              setClosingRecords(records);
            }}
            onPreviewSignature={(path, label) => setSignatureState({ path, label })}
          />
          <VisitList
            title="Storico visite"
            description="Archivio completo delle visite, dalla più recente."
            records={history.records}
            total={history.total}
            emptyMessage="Nessun record disponibile con i filtri correnti."
            isRefreshing={isRefreshing}
            isLoadingMore={isLoadingMore}
            onLoadMore={() => {
              handleLoadMore().catch(() => undefined);
            }}
            onPreviewSignature={(path, label) => setSignatureState({ path, label })}
            onEdit={(record) => {
              setEditErrorMessage(null);
              setEditingRecord(record);
            }}
          />
        </>
      ) : null}

      {signatureState ? (
        <SignaturePreviewModal
          path={signatureState.path}
          label={signatureState.label}
          onClose={() => setSignatureState(null)}
        />
      ) : null}

      {editingRecord ? (
        <VisitEditModal
          record={editingRecord}
          isSaving={isSaving}
          errorMessage={editErrorMessage}
          onClose={() => setEditingRecord(null)}
          onSave={handleSaveEdit}
        />
      ) : null}

      {closingRecords ? (
        <CloseVisitsModal
          records={closingRecords}
          isSaving={isSaving}
          errorMessage={closeErrorMessage}
          onClose={() => setClosingRecords(null)}
          onConfirm={handleConfirmClose}
        />
      ) : null}
    </AdminLayout>
  );
}
